/**
 * Trygg markdown-renderer for hjelpesystemet.
 * Bruker react-markdown + rehype-sanitize (allowlist, ingen HTML-injection).
 * Åpner alle eksterne lenker i ny fane med rel="noopener noreferrer".
 */
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';

const schema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    a: [
      ...(defaultSchema.attributes?.a || []),
      ['target'],
      ['rel'],
      ['className']
    ],
    code: [...(defaultSchema.attributes?.code || []), ['className']],
    span: [...(defaultSchema.attributes?.span || []), ['className']]
  },
  // Behold alle standard-elementer; vi legger ikke til noe eksotisk
};

interface Props {
  content: string;
  className?: string;
}

export default function MarkdownRenderer({ content, className = '' }: Props) {
  return (
    <div className={`prose prose-sm prose-slate max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, schema]]}
        components={{
          a: ({ href, children, ...rest }) => {
            const isExternal = typeof href === 'string' && /^https?:\/\//i.test(href);
            return (
              <a
                href={href}
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noopener noreferrer' : undefined}
                className="text-kf-blue-700 underline hover:text-kf-blue-900"
                {...rest}
              >
                {children}
              </a>
            );
          },
          h1: ({ children }) => <h1 className="text-xl font-semibold text-kf-gray-900 mt-4 mb-2">{children}</h1>,
          h2: ({ children }) => <h2 className="text-lg font-semibold text-kf-gray-900 mt-4 mb-2">{children}</h2>,
          h3: ({ children }) => <h3 className="text-base font-semibold text-kf-gray-900 mt-3 mb-1">{children}</h3>,
          p: ({ children }) => <p className="text-sm text-kf-gray-700 leading-relaxed mb-3">{children}</p>,
          ul: ({ children }) => <ul className="list-disc list-outside pl-5 text-sm text-kf-gray-700 mb-3 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-outside pl-5 text-sm text-kf-gray-700 mb-3 space-y-1">{children}</ol>,
          li: ({ children }) => <li className="pl-1">{children}</li>,
          code: ({ children, className }) => {
            const isBlock = className?.includes('language-');
            return isBlock ? (
              <code className="block bg-kf-gray-100 border border-kf-gray-200 rounded px-3 py-2 text-xs font-mono text-kf-gray-800 overflow-x-auto">{children}</code>
            ) : (
              <code className="bg-kf-gray-100 px-1 py-0.5 rounded text-xs font-mono text-kf-gray-800">{children}</code>
            );
          },
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-kf-blue-200 bg-kf-blue-50 pl-4 py-2 italic text-sm text-kf-gray-700 mb-3">
              {children}
            </blockquote>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto mb-3">
              <table className="min-w-full text-sm border border-kf-gray-200">{children}</table>
            </div>
          ),
          th: ({ children }) => <th className="border border-kf-gray-200 bg-kf-gray-50 px-2 py-1 text-left font-semibold text-xs">{children}</th>,
          td: ({ children }) => <td className="border border-kf-gray-200 px-2 py-1 text-sm">{children}</td>,
          hr: () => <hr className="my-4 border-kf-gray-200" />
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
